import { RightOutlined } from '@ant-design/icons';
import { Link, useParams } from '@tanstack/react-router';
import Loader from '@zeus/core/Loader';
import { useFlowContext } from '_/contexts/FlowContext';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { useFlowDestinationsData } from '_/hooks/useFlows';
import Collapse from 'antd/lib/collapse';
import Table from 'antd/lib/table';
import { ColumnsType } from 'antd/lib/table/interface';
import React, { CSSProperties, ReactElement, useEffect, useMemo } from 'react';

import FlowDestinationsCheckbox from './FlowDestinationsCheckbox';
import FlowDestinationsLinker from './FlowDestinationsLinker';
import FlowDestinationsPanelHeader from './FlowDestinationsPanelHeader';

const getTableColumns = (orgSlug: string): ColumnsType => [
  {
    title: 'Name',
    dataIndex: 'name',
    width: '55%',
    render: (name) => <Link to={`/orgs/${orgSlug}/destinations/${name}/edit`}>{name}</Link>,
  },
  {
    title: 'Type',
    dataIndex: 'type',
    width: '15%',
    align: 'center',
  },
  {
    title: 'Active',
    dataIndex: 'active',
    width: '15%',
    align: 'center',
    render: (active) => <FlowDestinationsCheckbox checked={active} disabled />,
  },
  {
    title: 'Linked',
    dataIndex: 'id',
    width: '15%',
    align: 'center',
    render: (id, { flowDestinationId, flowSegmentId, type, overrideDestinations }) => (
      <FlowDestinationsLinker
        destinationId={id}
        flowDestinationId={flowDestinationId}
        flowSegmentId={flowSegmentId}
        type={type}
        overrideDestinations={overrideDestinations}
      />
    ),
  },
];

const defaultActiveKey = ['1'];

const panelStyle: CSSProperties = { fontWeight: 'bold' };

const tableStyle: CSSProperties = { fontWeight: 'normal', overflowY: 'auto', maxHeight: '332px' };

const FlowDestinationsPage = (): ReactElement => {
  const { projectId, flowSlug, flowId, selectedFlow } = useFlowContext();
  const params = useParams({ strict: false }) as Partial<{ orgSlug: string; projectSlug: string }>;
  const orgSlug = params.orgSlug || '';
  const projectSlug = params.projectSlug || '';
  const { setBreadcrumbs } = useTopHeader();
  const { selectedOrganization } = useOrganization();
  const { selectedProject } = useProject();

  const { data: items = [], isLoading } = useFlowDestinationsData(projectId, flowSlug, flowId, orgSlug, projectSlug);
  const tableColumns = useMemo(() => getTableColumns(orgSlug), [orgSlug]);

  // Set up breadcrumbs
  useEffect(() => {
    setBreadcrumbs([
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${orgSlug}` },
      { title: 'Projects', href: `/orgs/${orgSlug}/projects` },
      { title: selectedProject?.name || '', href: `/orgs/${orgSlug}/projects/${projectSlug}/home` },
      { title: 'Flows', href: `/orgs/${orgSlug}/projects/${projectSlug}/flows` },
      {
        title: selectedFlow?.name || 'Flow',
        href: `/orgs/${orgSlug}/projects/${projectSlug}/flows/${flowSlug}`,
      },
      { title: 'Destinations' },
    ]);

    return () => {
      setBreadcrumbs([]);
    };
  }, [setBreadcrumbs, selectedOrganization, orgSlug, selectedProject, projectSlug, selectedFlow, flowSlug]);

  if (isLoading) {
    return <Loader />;
  }

  const collapseItems = items.map(({ segmentKey, href, destinations, header, flowSegmentId, overrideDestinations }) => ({
    key: segmentKey,
    label: (
      <FlowDestinationsPanelHeader
        flowSegmentId={flowSegmentId}
        header={header}
        href={href}
        overrideDestinations={overrideDestinations}
      />
    ),
    style: panelStyle,
    children: (
      <Table bordered columns={tableColumns} dataSource={destinations} pagination={false} rowKey="id" style={tableStyle} />
    ),
  }));

  return (
    <>
      <Collapse
        expandIconPlacement="start"
        defaultActiveKey={defaultActiveKey}
        expandIcon={({ isActive }) => (
          <div style={{ margin: '7px 12px 0 0' }}>
            <RightOutlined rotate={isActive ? 90 : 0} />
          </div>
        )}
        items={collapseItems}
      />
    </>
  );
};

export default FlowDestinationsPage;
